import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';

const STATUS_STYLE = {
  pending:  { color: '#b45309', bg: '#fffbeb', border: '#fde68a' },
  approved: { color: '#15803d', bg: '#f0fdf4', border: '#bbf7d0' },
  rejected: { color: '#dc2626', bg: '#fef2f2', border: '#fecaca' },
};

export default function AdminApplications() {
  const [apps, setApps]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [filter, setFilter]     = useState('pending');
  const [approving, setApproving] = useState(null); // application row
  const [form, setForm]         = useState({ username: '', password: '' });
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    try { setApps(await api.adminGetApplications()); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const approve = async () => {
    if (!form.username || !form.password) return setError('Username and password required.');
    setSaving(true); setError('');
    try {
      await api.adminApproveApplication(approving.id, form);
      setApproving(null);
      setForm({ username: '', password: '' });
      load();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const reject = async (id) => {
    try { await api.adminRejectApplication(id); load(); }
    catch (err) { setError(err.message); }
  };

  const filtered = apps.filter(a => {
    if (filter === 'pending') return a.status === 'pending' && a.verified;
    if (filter === 'unverified') return !a.verified;
    return a.status === filter;
  });

  const pendingCount = apps.filter(a => a.status === 'pending' && a.verified).length;

  if (loading) return <div style={{ color: '#64748b', padding: 40 }}>Loading…</div>;

  return (
    <div>
      <h1 style={s.h1}>Applications</h1>
      <p style={s.sub}>Review and approve restaurant applications.</p>

      {error && <div style={s.errBanner}>{error}</div>}

      {/* Filter tabs */}
      <div style={s.filterRow}>
        {[
          { key: 'pending',    label: 'Pending review', count: pendingCount },
          { key: 'unverified', label: 'Unverified' },
          { key: 'approved',   label: 'Approved' },
          { key: 'rejected',   label: 'Rejected' },
        ].map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            style={{ ...s.filterBtn, ...(filter === f.key ? s.filterActive : {}) }}>
            {f.label}
            {f.count > 0 && <span style={s.badge}>{f.count}</span>}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div style={s.empty}>No {filter} applications.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map(app => {
            const d = app.data;
            const st = STATUS_STYLE[app.status] || STATUS_STYLE.pending;
            const isOpen = expanded === app.id;
            return (
              <div key={app.id} style={s.card}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>{d.restaurantName}</span>
                      <span style={{ fontSize: 11, fontWeight: 700, background: st.bg, color: st.color,
                        border: `1px solid ${st.border}`, borderRadius: 5, padding: '2px 7px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {app.verified ? app.status : 'unverified'}
                      </span>
                    </div>
                    <div style={{ fontSize: 13, color: '#64748b' }}>
                      {d.ownerName} · {d.email} · {d.city}, {d.country}
                    </div>
                    <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
                      Applied {new Date(app.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                    <button style={s.detailBtn} onClick={() => setExpanded(isOpen ? null : app.id)}>
                      {isOpen ? 'Hide' : 'Details'}
                    </button>
                    {app.status === 'pending' && app.verified && (
                      <>
                        <button style={s.approveBtn} onClick={() => { setApproving(app); setForm({ username: '', password: '' }); setError(''); }}>
                          Approve
                        </button>
                        <button style={s.rejectBtn} onClick={() => reject(app.id)}>
                          Reject
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {isOpen && (
                  <div style={s.detailGrid}>
                    {[
                      ['Plan',       d.plan === 'launch' ? 'Seva Launch' : 'Seva Core'],
                      ['Cuisine',    d.cuisine === 'Other' ? d.cuisineOther : d.cuisine],
                      ['Covers',     d.covers],
                      ['Platform',   d.platform === 'Other' ? d.platformOther : d.platform],
                      ['Website',    d.websiteUrl || (d.urlMode === 'none' ? 'None provided' : '—')],
                      ['Phone',      d.phone || '—'],
                      ['Referral',   d.referral === 'Other' ? d.referralOther : d.referral],
                      ['Setup fee',  d.setupFee ? 'Yes' : 'No'],
                    ].map(([k, v]) => (
                      <div key={k} style={s.detailItem}>
                        <div style={s.detailKey}>{k}</div>
                        <div style={s.detailVal}>{v || '—'}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Approve modal */}
      {approving && (
        <div style={s.overlay} onClick={() => setApproving(null)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <h2 style={s.modalTitle}>Approve — {approving.data.restaurantName}</h2>
            <p style={{ fontSize: 13, color: '#64748b', margin: '6px 0 20px' }}>
              Set login credentials for this restaurant. These will be emailed to {approving.data.email}.
            </p>
            {error && <div style={s.errBanner}>{error}</div>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={s.label}>Username</label>
                <input style={s.input} type="text" autoFocus value={form.username}
                  onChange={e => setForm(f => ({ ...f, username: e.target.value }))} />
              </div>
              <div>
                <label style={s.label}>Password</label>
                <input style={s.input} type="text" value={form.password}
                  onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <button style={s.approveBtn} onClick={approve} disabled={saving}>
                {saving ? 'Creating…' : 'Create account & send email'}
              </button>
              <button style={s.cancelBtn} onClick={() => setApproving(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  h1:       { fontSize: 28, fontWeight: 800, color: '#0f172a', margin: 0, letterSpacing: '-0.5px' },
  sub:      { fontSize: 14, color: '#64748b', marginTop: 4, marginBottom: 24 },
  errBanner:{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 10, padding: '10px 14px', fontSize: 13, marginBottom: 16 },
  filterRow:{ display: 'flex', gap: 6, marginBottom: 20, flexWrap: 'wrap' },
  filterBtn:{ background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 8, padding: '7px 14px', fontSize: 13, cursor: 'pointer', color: '#475569', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6 },
  filterActive: { background: '#4f46e5', borderColor: '#4f46e5', color: '#fff' },
  badge:    { background: '#ef4444', color: '#fff', borderRadius: 10, padding: '1px 6px', fontSize: 11, fontWeight: 700 },
  empty:    { color: '#94a3b8', fontSize: 14, padding: '40px 0', textAlign: 'center' },
  card:     { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, padding: '18px 20px', boxShadow: '0 1px 3px rgba(15,23,42,0.05)' },
  detailGrid:{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginTop: 16, padding: '16px 0 4px', borderTop: '1px solid #f1f5f9' },
  detailItem:{ display: 'flex', flexDirection: 'column', gap: 2 },
  detailKey: { fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.07em' },
  detailVal: { fontSize: 13, color: '#0f172a', fontWeight: 500 },
  detailBtn: { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 7, padding: '6px 12px', fontSize: 12, cursor: 'pointer', color: '#475569' },
  approveBtn:{ background: '#4f46e5', border: 'none', borderRadius: 7, padding: '6px 14px', fontSize: 12, fontWeight: 700, color: '#fff', cursor: 'pointer' },
  rejectBtn: { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 7, padding: '6px 12px', fontSize: 12, color: '#dc2626', cursor: 'pointer' },
  cancelBtn: { background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 10, padding: '11px 20px', fontSize: 14, color: '#475569', cursor: 'pointer' },
  overlay:  { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500 },
  modal:    { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 20, padding: '28px 32px', width: 440, maxWidth: '90vw', boxShadow: '0 20px 60px rgba(15,23,42,0.18)' },
  modalTitle:{ fontSize: 18, fontWeight: 800, color: '#0f172a', margin: 0 },
  label:    { display: 'block', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 },
  input:    { width: '100%', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 13px', fontSize: 14, outline: 'none', color: '#0f172a', boxSizing: 'border-box' },
};
